import React, { useRef, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { stateToHTML } from 'draft-js-export-html';

import CCEvents, { EventNames } from '@companycam/companycam-events';

import { Preview, Editor } from 'components/shared/RichTextEditor';
import { getTextFromHTML } from 'components/shared/RichTextEditor/utils';
import { QuestionOutlineIcon } from 'components/shared/DesignSystem/Icons';

import useAssetDescription from './useAssetDescription';
import { ButtonCondensed, Spinner } from '@companycam/slab-web';
import { AssetModalSidebarHeader } from '../shared/styles';
import { translate } from 'translations/i18n';

// #region styled-components
const DescriptionWrapper = styled.div`
  padding-bottom: var(--cc_size_spacing_m);
`;

const ScrollingPreviewDiv = styled.div`
  display: flex;
  min-height: 28px;
  padding: 7px 24px 0;
  word-break: break-word;
`;

const GhostInput = styled.button`
  color: var(--cc_color_text_nonessential);
  font-family: inherit;
  border: none;
  background: none;
  padding: 0;
  cursor: text;
  opacity: 0.75;
  margin: 2.5px 0 0;

  .asset-meta-container & {
    font-size: var(--cc_size_text_s);
  }
`;

const ButtonDiv = styled.div`
  display: flex;
  align-items: center;
`;

const HintDiv = styled.div`
  margin: 0 auto 0 8px;
`;

const SavedText = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--cc_color_utility_success);

  i {
    font-size: 14px;
    margin-right: 4px;
  }
`;

const GhostButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  margin-top: 1px;
  margin-left: 0.5px;
  font: inherit;
  cursor: text;
  outline: inherit;
  opacity: 0.75;
  text-align: left;

  &:hover {
    opacity: 1;
    transition: all ease-in-out 150ms;
  }
`;
// #endregion

const ACTION_START_EDITING = 'ACTION_START_EDITING';
const ACTION_UPDATE_EDITED_CONTENT = 'ACTION_UPDATE_EDITED_CONTENT';
const ACTION_UPDATE_SAVED_CONTENT = 'ACTION_UPDATE_SAVED_CONTENT';
const ACTION_STOP_EDITING = 'ACTION_STOP_EDITING';

const INITIAL_EDITING_STATE = {
  isEditing: false,
  assetNodeId: null,
  initialContent: '',
  savedContent: '',
  editedContent: '',
};

const editedStateReducer = (state, action) => {
  switch (action.type) {
    case ACTION_START_EDITING: {
      const { assetNodeId, content } = action;
      return {
        isEditing: true,
        assetNodeId,
        initialContent: content,
        savedContent: content,
        editedContent: content,
      };
    }
    case ACTION_UPDATE_EDITED_CONTENT: {
      const { content } = action;
      return { ...state, editedContent: content };
    }
    case ACTION_UPDATE_SAVED_CONTENT: {
      const { content } = action;
      return { ...state, savedContent: content };
    }
    case ACTION_STOP_EDITING: {
      return { ...state, isEditing: false };
    }
    default:
      return state;
  }
};

const AssetDescription = ({ assetNodeId, setKeyboardNavigationEnabled }) => {

  const refHeader = useRef();

  const { isLoading, description, setDescription } = useAssetDescription({
    nodeId: assetNodeId,
  });

  const [editingState, dispatch] = useReducer(
    editedStateReducer,
    INITIAL_EDITING_STATE,
  );

  const doEventOnUnfocus = useRef(false);

  const unsavedChanges =
    editingState.editedContent !== editingState.savedContent;

  const optimisticDescription =
    unsavedChanges && editingState.assetNodeId === assetNodeId
      ? editingState.editedContent
      : description;

  const stopEditing = useCallback(() => {
    dispatch({ type: ACTION_STOP_EDITING });
    // NOTE: This is to work around a weird bug with draft-js in firefox.
    // If the gallery arrows are focused, and then the draft-js gets focus,
    // draft-js will crash on selection.extend in addFocusToSelection.
    // I don't know why.  But, pre-emptively focusing something here fixes it.
    if (refHeader.current) {
      refHeader.current.focus();
    }
  }, []);

  useEffect(
    () => {
      const debounceMS = editingState.isEditing ? 750 : 1;
      const timerId = setTimeout(async () => {
        if (!unsavedChanges) {
          return;
        }
        doEventOnUnfocus.current = true;
        const newContent = editingState.editedContent;
        await setDescription({
          assetNodeId: editingState.assetNodeId,
          content: newContent,
        });
        dispatch({
          type: ACTION_UPDATE_SAVED_CONTENT,
          content: newContent,
        });
      }, debounceMS);

      return () => clearTimeout(timerId);
    },
    [editingState, unsavedChanges, setDescription],
  );

  useEffect(
    () => {
      if (editingState.isEditing && editingState.assetNodeId !== assetNodeId) {
        stopEditing();
      }
    },
    [editingState, stopEditing, assetNodeId],
  );

  useEffect(
    () => {
      setKeyboardNavigationEnabled(!editingState.isEditing);
    },
    [editingState, setKeyboardNavigationEnabled],
  );

  useEffect(
    () => {
      if (!editingState.isEditing && doEventOnUnfocus.current) {
        doEventOnUnfocus.current = false;
        CCEvents.logEvent(EventNames.DESCRIPTION_SAVED);
      }
    },
    [editingState],
  );

  const button = editingState.isEditing ? (
    <SavedText>
      <i className="mdi mdi-cloud-check" />
      {translate('assets.description.saved')}
    </SavedText>
  ) : (
    <ButtonCondensed
      data-testid="asset__description-edit-button"
      color="primary"
      size="small"
      disabled={isLoading || unsavedChanges}
      onClick={() => {
        dispatch({
          type: ACTION_START_EDITING,
          assetNodeId,
          content: description,
        });
      }}
    >
      {translate('common.edit')}
    </ButtonCondensed>
  );

  let notePreview = <Spinner size={20} accessibilityLabel={translate('common.loading')} />;
  if (!isLoading) {
    notePreview = getTextFromHTML(optimisticDescription) ? (
      <GhostButton
        type="button"
        onClick={() => {
          dispatch({
            type: ACTION_START_EDITING,
            assetNodeId,
            content: optimisticDescription,
          });
        }}
      >
        <Preview contentHTML={optimisticDescription} />
      </GhostButton>
    ) : (
      <GhostInput
        data-testid="asset__add-description-button"
        type="button"
        onClick={() => {
          dispatch({
            type: ACTION_START_EDITING,
            assetNodeId,
            content: '',
          });
        }}
      >
        {translate('assets.description.addDescription')}
      </GhostInput>
    );
  }

  const content = editingState.isEditing ? (
    <Editor
      autoFocus
      placeholder={translate('assets.description.addDescription')}
      directUseSnippets={false}
      initialContents={editingState.initialContent}
      snippetsVisible
      snippetsEnabled
      onChange={(_oldState, newState) => {
        const contentRaw = newState.getCurrentContent();
        const contentHTML = stateToHTML(contentRaw);
        dispatch({
          type: ACTION_UPDATE_EDITED_CONTENT,
          content: contentHTML,
        });
      }}
      onBlur={(_editorState, isEditorCloseable) => {
        if (isEditorCloseable) {
          stopEditing();
        }
      }}
    />
  ) : (
    <ScrollingPreviewDiv>{notePreview}</ScrollingPreviewDiv>
  );

  return (
    <DescriptionWrapper>
      <AssetModalSidebarHeader
        tabIndex="-1"
        ref={refHeader}
        data-testid="asset__description-header"
      >
        {translate('assets.description.header')}
        <HintDiv
          className="hint--top-right hint--medium"
          aria-label={translate('assets.description.descriptionHint')}
        >
          <QuestionOutlineIcon color="var(--cc_color_text_subtle)" />
        </HintDiv>
        <ButtonDiv>
          {unsavedChanges ? (
            <Spinner size="tiny" meterColor="var(--cc_color_background_3)" />
          ) : (
            button
          )}
        </ButtonDiv>
      </AssetModalSidebarHeader>
      {content}
    </DescriptionWrapper>
  );
};

AssetDescription.propTypes = {
  assetNodeId: PropTypes.string.isRequired,
  setKeyboardNavigationEnabled: PropTypes.func,
};

AssetDescription.defaultProps = {
  setKeyboardNavigationEnabled: () => {},
};

export default AssetDescription;
